// File builder for the Sales Intel Prep Pack download: a ZIP with the
// Brand Intelligence PDF at the root, a pdp-audits/ folder holding one PDF
// per successfully audited product URL, a consumer-voice/ folder holding
// one Consumer Voice PDF per analyzed product, and an ai-presence/ folder
// holding one AI Presence PDF per analyzed product. Mirrors the PdpAudit module's
// buildMultiReportZip (all PDFs render client-side via @react-pdf/renderer).

import React from "react";
import JSZip from "jszip";
import { pdf } from "@react-pdf/renderer";
import { PrepPackPdf } from "./PrepPackPdf";
import { PdpAuditPdf } from "../PdpAudit/PdpAuditPdf";
import { rowToResponse, yieldToUi } from "../PdpAudit/reportFiles";
import { buildConsumerVoicePdfBlob } from "../ConsumerVoice/consumerVoiceFiles";
import { cvSubjectLabel } from "../../types/consumerVoice";
import { buildAiPresencePdfBlob } from "../AiPresence/aiPresenceFiles";
import { aipSubjectLabel } from "../../types/aiPresence";
import { PrepPack, isV2Report } from "../../pages/sales/prepPacks/types";
import { slugify } from "../../utils";

// Pre-fetch expert avatars as data URLs for the v2 Recommended Experts
// page. Raw remote URLs must never reach react-pdf's <Image> — one dead URL
// (e.g. an expired Typeform upload link) fails the WHOLE render. Each fetch
// is individually try/caught with a timeout; failures just mean that expert
// renders with an initials circle. CloudFront avatars send CORS `*`, so the
// browser fetch works for the normal case.
const AVATAR_FETCH_TIMEOUT_MS = 8000;
// Avatars render in a 44pt circle; originals can be multi-MB. Downscale to
// a small JPEG so embedding three headshots doesn't triple the PDF size.
const AVATAR_TARGET_PX = 128;

async function downscaleToDataUrl(blob: Blob): Promise<string> {
    const bitmap = await createImageBitmap(blob);
    const scale = Math.min(
        1,
        AVATAR_TARGET_PX / Math.min(bitmap.width, bitmap.height),
    );
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("no 2d context");
    ctx.drawImage(bitmap, 0, 0, w, h);
    return canvas.toDataURL("image/jpeg", 0.85);
}

export async function fetchExpertImages(
    pack: PrepPack,
): Promise<Record<number, string>> {
    if (!isV2Report(pack.report)) return {};
    const experts = pack.report.experts || [];
    const out: Record<number, string> = {};
    await Promise.all(
        experts
            .filter((e) => e.imageUrl)
            .map(async (e) => {
                try {
                    const controller = new AbortController();
                    const t = setTimeout(
                        () => controller.abort(),
                        AVATAR_FETCH_TIMEOUT_MS,
                    );
                    const res = await fetch(e.imageUrl as string, {
                        signal: controller.signal,
                    });
                    clearTimeout(t);
                    if (!res.ok) return;
                    const blob = await res.blob();
                    if (!blob.type.startsWith("image/")) return;
                    try {
                        out[e.userid] = await downscaleToDataUrl(blob);
                    } catch {
                        // Odd format the canvas can't decode — embed as-is.
                        out[e.userid] = await new Promise<string>(
                            (resolve, reject) => {
                                const reader = new FileReader();
                                reader.onload = () =>
                                    resolve(reader.result as string);
                                reader.onerror = reject;
                                reader.readAsDataURL(blob);
                            },
                        );
                    }
                } catch {
                    // Initials fallback — never block the download.
                }
            }),
    );
    return out;
}

// Named by the pack's product-focus title ("Brand — Product focus"); older
// packs without one fall back to the (possibly corrected) parent company.
export const prepPackFileBase = (pack: PrepPack): string => {
    const name =
        pack.title ||
        (isV2Report(pack.report)
            ? pack.report.doc.meta.parent_company
            : undefined) ||
        pack.brand_name ||
        pack.brand?.name;
    return `${slugify(name, "prep-pack")}-sales-intel-prep-pack`;
};

// Requires the pack detail payload (audits and Consumer Voice reports are
// only included on the item GET, not the list). Packs with no successful
// audits — including legacy catalog-product packs — produce a ZIP with just
// the Brand Intelligence PDF. Both surfaces get every PDF; only the QA
// diagnostics are internal.
// `internal` gates diagnostics (QA verdict on the cover, qa-findings.txt):
// the RMN white-label surface passes false so brand users never see them.
export async function buildPrepPackZip(
    pack: PrepPack,
    { internal = true }: { internal?: boolean } = {},
): Promise<Blob> {
    const zip = new JSZip();
    const expertImages = await fetchExpertImages(pack);
    zip.file(
        "brand-intelligence.pdf",
        await pdf(
            <PrepPackPdf
                pack={pack}
                internal={internal}
                expertImages={expertImages}
            />,
        ).toBlob(),
    );

    // Internal downloads carry the QA gate's raw findings for the rep.
    if (internal && isV2Report(pack.report) && pack.report.qa?.findings) {
        zip.file(
            "qa-findings.txt",
            `QA verdict: ${pack.report.qa.verdict}\n\n${pack.report.qa.findings}`,
        );
    }

    const audited = (pack.audits || []).filter((a) => a.report);
    if (audited.length > 0) {
        const folder = zip.folder("pdp-audits");
        for (let i = 0; i < audited.length; i += 1) {
            const audit = audited[i];
            // Sequential on purpose — parallel renders contend for the main
            // thread and hold every intermediate PDF in memory at once.
            // eslint-disable-next-line no-await-in-loop
            const blob = await pdf(
                <PdpAuditPdf
                    data={rowToResponse({
                        id: audit.id,
                        url: audit.url,
                        report_type: "pdp",
                        report: audit.report,
                    })}
                />,
            ).toBlob();
            // Index prefix keeps the audit order and prevents JSZip silently
            // overwriting duplicate product names; the type prefix keeps the
            // three report kinds apart once files leave their folders.
            const name = `PDP-Audit-${String(i + 1).padStart(2, "0")}-${slugify(
                audit.report?.productName || audit.url,
                "audit",
            )}.pdf`;
            folder?.file(name, blob);
            // eslint-disable-next-line no-await-in-loop
            await yieldToUi();
        }
    }

    const analyzed = (pack.cv_audits || []).filter(
        (a) => a.status === "ready" && a.report,
    );
    if (analyzed.length > 0) {
        const folder = zip.folder("consumer-voice");
        for (let i = 0; i < analyzed.length; i += 1) {
            const audit = analyzed[i];
            // Sequential for the same reason as the PDP audits above.
            // eslint-disable-next-line no-await-in-loop
            const blob = await buildConsumerVoicePdfBlob(audit);
            const name = `CV-Audit-${String(i + 1).padStart(2, "0")}-${slugify(
                cvSubjectLabel(audit) || audit.url,
                "consumer-voice",
            )}.pdf`;
            folder?.file(name, blob);
            // eslint-disable-next-line no-await-in-loop
            await yieldToUi();
        }
    }

    const presence = (pack.aip_audits || []).filter((a) => a.status === "ready" && a.report);
    if (presence.length > 0) {
        const folder = zip.folder("ai-presence");
        for (let i = 0; i < presence.length; i += 1) {
            const audit = presence[i];
            // eslint-disable-next-line no-await-in-loop
            const blob = await buildAiPresencePdfBlob(audit);
            const name = `AI-Audit-${String(i + 1).padStart(2, "0")}-${slugify(
                aipSubjectLabel(audit) || audit.url,
                "ai-presence",
            )}.pdf`;
            folder?.file(name, blob);
            // eslint-disable-next-line no-await-in-loop
            await yieldToUi();
        }
    }

    return zip.generateAsync({ type: "blob", compression: "DEFLATE" });
}
